import { z } from 'zod';

type CustomRefinementCtx = z.RefinementCtx & {
  parent?: {
    resourceReferred?: boolean;
  };
};

export const ResourceDetailsFormSchema = z
  .object({
    id: z.number().optional(),
    resourceName: z.string().min(1, 'Resource name is required'),
    contactName: z.string().optional(),
    phoneNumber: z.string().optional().nullable(),
    url: z.string().optional(),
    streetAddress: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipcode: z.string().optional(),
    shouldDelete: z.boolean().optional(),
    googlePlaceId: z.string().optional().nullable(),
    email: z.string().optional(),
    formattedAddress: z.string().optional(),
    addressDetails: z.string().optional(),
    placeTypes: z.array(z.string()).optional(),
    categoryLabel: z.string().optional(),
    cboRecordId: z.string().optional(),
    /** Manual resource entry; skips CBO cache persistence. */
    isCustomResource: z.boolean().optional(),
  })
  .superRefine((data, ctx: CustomRefinementCtx) => {
    const parentData = ctx?.parent;
    if (parentData?.resourceReferred && !data.resourceName) {
      ctx.addIssue({
        path: ['resourceName'],
        message: "Resource name is required when 'resourceReferred' is true.",
        code: z.ZodIssueCode.custom,
      });
    }
  });

export const NeedActionFormSchema = z
  .object({
    activityId: z.number().int().positive('Activity is required'),
    date: z.string(),
    duration: z.string().nonempty('Duration is required'),
    description: z.string().nonempty('Description is required'),
    resourceReferred: z.boolean().optional(),
    markAsFulfilled: z.boolean().optional(),
    resourceDetails: z.array(ResourceDetailsFormSchema).optional(),
    tempNeedId: z.string().optional(),
    suggestedActionId: z.string().optional(),
  })
  .superRefine((data, ctx: z.RefinementCtx) => {
    if (data.resourceReferred && !data.resourceDetails?.length) {
      ctx.addIssue({
        path: ['resourceDetails'],
        message:
          "Resource details are required when 'resourceReferred' is true.",
        code: z.ZodIssueCode.custom,
      });
    }
  });

export type ActionFormResourceDetails = z.infer<
  typeof ResourceDetailsFormSchema
>;
export type ActionFormValues = z.infer<typeof NeedActionFormSchema>;
